import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { GeofenceType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import Label from '~components/FormFields/v2/Label';
import { Site } from '~hooks/useSites/models';

import { defaultPolygonAround, GeofenceOverride, SiteOverride } from './geofence-utils';
import { GeofenceAccordionItem } from './GeofenceAccordionItem';

interface SiteOverridePanelProps {
  override: SiteOverride;
  setOverride: (
    update: Partial<SiteOverride> | ((prev: SiteOverride) => SiteOverride),
  ) => void;
  /** View-only selection of which geofence is highlighted on the map. Does not mark the form dirty. */
  onSelectGeofence: (gfIndex: number) => void;
  jobSite: Site | null;
  isEditable: boolean;
  isFeet: boolean;
  loadTriggers: () => void;
  /** Show usage info (loads per waypoint type) regardless of usage count. */
  showUsages?: boolean;
  /** Total distinct loads in the job — usages with this many loads are "all loads" and skip the list. */
  totalLoadCount?: number;
}

export function SiteOverridePanel({
  override,
  setOverride,
  onSelectGeofence,
  jobSite,
  isEditable,
  isFeet,
  loadTriggers,
  showUsages,
  totalLoadCount,
}: SiteOverridePanelProps) {
  const updateGeofence = useCallback(
    (gfIndex: number, patch: Partial<GeofenceOverride>) => {
      setOverride((prev) => {
        const geofences = prev.geofences.map((gf, i) =>
          i === gfIndex ? { ...gf, ...patch } : gf,
        );
        return { ...prev, geofences };
      });
    },
    [setOverride],
  );

  const updateGeofenceFn = useCallback(
    (gfIndex: number, fn: (prev: GeofenceOverride) => GeofenceOverride) => {
      setOverride((prev) => {
        const geofences = prev.geofences.map((gf, i) => (i === gfIndex ? fn(gf) : gf));
        return { ...prev, geofences };
      });
    },
    [setOverride],
  );

  const removeGeofence = useCallback(
    (gfIndex: number) => {
      setOverride((prev) => {
        const geofences = prev.geofences.filter((_, i) => i !== gfIndex);
        const selectedGeofenceIndex = Math.min(
          prev.selectedGeofenceIndex,
          geofences.length - 1,
        );
        return { ...prev, geofences, selectedGeofenceIndex };
      });
      loadTriggers();
    },
    [setOverride, loadTriggers],
  );

  const addGeofence = useCallback(() => {
    const siteCenter =
      typeof jobSite?.lat === 'number' && typeof jobSite?.lon === 'number'
        ? { lat: jobSite.lat, lng: jobSite.lon }
        : null;
    setOverride((prev) => {
      const newGf: GeofenceOverride = {
        key: crypto.randomUUID(),
        tag: null,
        geofenceType: GeofenceType.POLYGON,
        polygonCoordinates: siteCenter ? defaultPolygonAround(siteCenter) : [],
        circleCenter: null,
        circleRadius: null,
      };
      const geofences = [...prev.geofences, newGf];
      return {
        ...prev,
        geofences,
        selectedGeofenceIndex: geofences.length - 1,
      };
    });
  }, [setOverride, jobSite]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Label sx={{ mb: 0.5 }}>{t('admin.job_geo_debug.site_name_label')}</Label>
      <Typography variant="body2">
        {jobSite?.name || t('admin.job_geo_debug.no_site')}
      </Typography>
      <Label sx={{ mb: 0.5, mt: 1.5 }}>{t('form_fields.address')}</Label>
      <Typography variant="body2">
        {jobSite?.fullAddress || t('admin.job_geo_debug.no_site')}
      </Typography>
      {showUsages &&
        override.usages &&
        override.usages.length > 0 &&
        !override.usages.every(
          (u) => totalLoadCount !== undefined && u.loads.length === totalLoadCount,
        ) && (
          <>
            <Label sx={{ mb: 0.5, mt: 1.5 }}>
              {t('admin.job_geo_debug.usages_label')}
            </Label>
            {override.usages.map((usage) => {
              const isAllLoads =
                totalLoadCount !== undefined && usage.loads.length === totalLoadCount;
              return (
                <Box key={usage.waypointType} sx={{ mb: 0.5 }}>
                  {override.usages!.length > 1 && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {t(`waypoint_type.${usage.waypointType}`)}
                    </Typography>
                  )}
                  {!isAllLoads && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {usage.loads
                        .slice(0, 3)
                        .map((l) => l.loadId)
                        .join(', ')}
                      {usage.loads.length > 3 &&
                        ` (+${t('admin.job_geo_debug.load_count', { count: usage.loads.length - 3 })})`}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </>
        )}
      <Label sx={{ mb: 0.5, mt: 1.5 }}>{t('admin.job_geo_debug.geofences_label')}</Label>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {override.geofences.map((gf, i) => (
          <GeofenceAccordionItem
            key={gf.key}
            gf={gf}
            isSelected={i === override.selectedGeofenceIndex}
            isEditable={isEditable}
            isFeet={isFeet}
            jobSite={jobSite}
            onSelect={() => onSelectGeofence(i)}
            onUpdate={(patch) => updateGeofence(i, patch)}
            onUpdateFn={(fn) => updateGeofenceFn(i, fn)}
            onRemove={isEditable && i > 0 ? () => removeGeofence(i) : null}
            loadTriggers={loadTriggers}
          />
        ))}
      </Box>
      {isEditable && (
        <Button
          size="small"
          startIcon={<Add />}
          onClick={addGeofence}
          sx={{ mt: 0.5, alignSelf: 'flex-start' }}
        >
          {t('admin.job_geo_debug.add_geofence')}
        </Button>
      )}
    </Box>
  );
}
